import PolarisIcon from '@/components/_common/PolarisIcon';
import styled from '@emotion/styled';
import { Box, Tooltip } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import React, { useState } from 'react';
import { selectOrderConstants } from '../../redux/selectors/constantsSelectors';
import Autocomplete from './Form/Autocomplete';
import { useAppSelector } from '@/redux/hooks';

interface CustomerSearchProps {
  value: string;
  label?: string;
  canAddCustomer: boolean;
  onChange: (value: string) => void;
  error?: boolean;
}

interface CustomerOption {
  title: string;
  inputValue?: string;
}

const filter = createFilterOptions();

const CustomerSearch = ({ value, label = 'Customer', canAddCustomer, onChange, error = false }: CustomerSearchProps) => {
  const { customers = {} } = useAppSelector(selectOrderConstants);
  const customerOptions: CustomerOption[] = Object.keys(customers)?.map((customer) => ({ title: customer })) || [];
  const [newOption, setNewOption] = useState<CustomerOption | null>(null);

  const handleOnChange = (_e: React.SyntheticEvent<Element, Event>, newValue: CustomerOption) => {
    if (newValue?.inputValue) {
      onChange(newValue.inputValue);
      setNewOption({ title: newValue.inputValue });
    } else {
      setNewOption(null);
      onChange(newValue.title);
    }
  };

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: CustomerOption) => {
    return <li {...props}>{newOption === option ? <em style={{ opacity: 0.8 }}>{option.title}</em> : option.title}</li>;
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Autocomplete
        label={label}
        options={!newOption ? customerOptions : [newOption, ...customerOptions]}
        value={value}
        id="customers_search"
        onChange={handleOnChange}
        disableClearable
        handleHomeEndKeys
        freeSolo
        clearOnBlur
        inputProps={{ InputLabelProps: { shrink: true, required: true } }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          if (canAddCustomer) {
            const { inputValue } = params;
            // Suggest the creation of a new value
            const isExisting = options.some((option) => inputValue === option.title);
            if (inputValue !== '' && !isExisting) filtered.push({ inputValue, title: `Add "${inputValue}"` });
          }

          return filtered;
        }}
        getOptionLabel={(option) => {
          // Value selected with enter, right from the input
          if (typeof option === 'string') return option;
          // Add "xxx" option created dynamically
          if (option.inputValue) return option.inputValue;
          // Regular option
          return option.title;
        }}
        renderOption={renderOption}
        placeholder="Start typing to search customers"
        error={error}
      />
      {newOption && newOption.title === value && (
        <Tooltip title="Customer will be created when saved to profile">
          <StyledIcon icon={'question_filled'} height={20} />
        </Tooltip>
      )}
    </Box>
  );
};

export default CustomerSearch;

const StyledIcon = styled(PolarisIcon)`
  .MuiTooltip-root {
    color: ${({ theme }) => theme.palette.primary.main};
    position: absolute;
    right: ${({ theme }) => theme.spacing(2)};
    top: calc(50% - 10px);
  }
`;
